'use client'

import { useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { createClient } from '@/lib/supabase/client'

/**
 * Identifies the current Supabase user in PostHog so sessions
 * are linked to real persons. Rendered once in the root layout;
 * the identify call is idempotent and only fires when the user
 * changes (login / logout).
 */
export function PostHogIdentify() {
  const identifiedRef = useRef<string | null>(null)

  useEffect(() => {
    const supabase = createClient()

    async function identify() {
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user && user.id !== identifiedRef.current) {
        posthog.identify(user.id, {
          email: user.email,
          name: user.user_metadata?.full_name,
        })
        identifiedRef.current = user.id
      } else if (!user && identifiedRef.current) {
        posthog.reset()
        identifiedRef.current = null
      }
    }

    identify()

    // Re-identify on auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user && session.user.id !== identifiedRef.current) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name,
        })
        identifiedRef.current = session.user.id
      } else if (!session?.user && identifiedRef.current) {
        posthog.reset()
        identifiedRef.current = null
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  return null
}
